// deps
import React, { memo, ReactElement, useCallback, useMemo } from "react";

// internal deps
import { NETBANKING_PAYMENT_METHOD_CODE } from "@swiggy-private/payment-core";

// interfaces
import {
    INetBankingPaymentGroup,
    NetBankingPaymentGroupMeta,
    NetBankingPaymentMethodData,
} from "@payment-ui/interfaces/NetBanking";
import { PaymentMethodProps } from "@payment-ui/interfaces";

// helpers
import {
    canEnablePaymentMethod,
    getPaymentMethodDisplayName,
    getPaymentMethodDiscount,
    getPaymentMethodIconUrl,
} from "@payment-ui/helpers/Payment";

//components
import PaymentMethodIcon from "@payment-ui/widgets/payment-method-icon/PaymentMethodIcon";
import OtherPaymentOffersGenericUI from "../../OtherPaymentOffersGenericUI";

const NETBANKING_ICON_WIDTH = 32;

interface Props extends PaymentMethodProps {
    key: string;
    methodData: NetBankingPaymentMethodData;
    paymentActions: INetBankingPaymentGroup["paymentActions"];
    paymentAmount: string;
    paymentGroupMeta?: NetBankingPaymentGroupMeta;
    isPreferredMethod?: boolean;
    showListView?: boolean;
}

function NetBankingPaymentMethod(props: Props): React.ReactElement {
    const { methodData, isPaymentGroupEnabled, paymentActions, paymentGroupMeta } = props;

    const isMethodEnabled = useMemo(() => canEnablePaymentMethod(methodData, isPaymentGroupEnabled), [
        isPaymentGroupEnabled,
        methodData,
    ]);

    const onPay = useCallback((): void => {
        if (!isMethodEnabled) {
            return;
        }

        paymentActions.onPay(methodData, {
            bankCode: methodData.nbCode,
            bankName: methodData.name,
            merchantId: paymentGroupMeta?.merchantId,
            paymentMethodType: NETBANKING_PAYMENT_METHOD_CODE,
        });
    }, [isMethodEnabled, paymentActions, methodData, paymentGroupMeta]);

    const displayName = getPaymentMethodDisplayName(props.methodData);
    const subtext = getPaymentMethodDiscount(props.methodData);

    const paymentMethodIcon = useMemo(
        (): ReactElement => (
            <PaymentMethodIcon
                src={getPaymentMethodIconUrl(props.methodData)}
                alt={props.methodData.displayName}
                width={NETBANKING_ICON_WIDTH}
            />
        ),
        [props.methodData],
    );

    return (
        <OtherPaymentOffersGenericUI
            onClick={onPay}
            displayName={displayName}
            subtext={subtext}
            paymentMethodIcon={paymentMethodIcon}
        />
    );
}

export default memo(NetBankingPaymentMethod);
